/**
 * useTurnTimer - Hook für Rundenzeit-Countdown
 *
 * Synchronisiert mit Server-Timer über turnStartTime.
 * Zeigt verbleibende Zeit basierend auf Server-Timestamp.
 */

import { useState, useEffect, useCallback } from 'react';
import soundService from '../soundService';

/**
 * @param {Object} config - Konfiguration
 * @param {number|null} config.turnTimeLimit - Zeit in Sekunden (null = aus)
 * @param {number|null} config.turnStartTime - Server-Timestamp wann Zug begann
 * @param {boolean} config.isMyTurn - Ob der aktuelle Spieler dran ist
 * @param {boolean} config.isPlaying - Ob in Spielphase (nicht Exchange/Shop)
 */
export function useTurnTimer({
  turnTimeLimit,
  turnStartTime,
  isMyTurn,
  isPlaying = true,
}) {
  const [timeLeft, setTimeLeft] = useState(null);

  // Calculate time left based on server timestamp
  const calculateTimeLeft = useCallback(() => {
    if (!turnTimeLimit || !turnStartTime || !isPlaying) {
      return null;
    }

    const elapsed = Math.floor((Date.now() - turnStartTime) / 1000);
    const remaining = Math.max(0, turnTimeLimit - elapsed);
    return remaining;
  }, [turnTimeLimit, turnStartTime, isPlaying]);

  useEffect(() => {
    if (!turnTimeLimit || !turnStartTime || !isPlaying) {
      setTimeLeft(null);
      return;
    }

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      // Play warning sounds only if it's my turn
      if (isMyTurn && remaining !== null) {
        if (remaining === 10) {
          soundService.play('timer_warning');
        }
        if (remaining <= 5 && remaining > 0) {
          soundService.play('timer_tick');
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [turnTimeLimit, turnStartTime, isPlaying, isMyTurn, calculateTimeLeft]);

  // Format time as M:SS
  const formattedTime = timeLeft !== null
    ? `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`
    : null;

  // Warning state (last 10 seconds)
  const isWarning = timeLeft !== null && timeLeft <= 10;

  // Critical state (last 5 seconds)
  const isCritical = timeLeft !== null && timeLeft <= 5;

  return {
    timeLeft,
    formattedTime,
    isWarning,
    isCritical,
    isActive: timeLeft !== null,
  };
}

export default useTurnTimer;
